import React, { ReactNode, useCallback } from 'react'

import { PaymentSubmitCallback } from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/typings/CheckoutFlowTypes'
import { BillwerkRedirectButton }
  from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Components/ProviderComponents/Billwerk/Components/Redirect/BillwerkRedirect'
import { getDeviceType } from '@Globals/Device'
import { CheckoutCartV2Total, CheckoutV2PaymentMethod } from '@Pages/onestepcheckout'
import { AdyenApplePayButton } from '@PaymentComponents/Adyen/Components/ApplePay/AdyenApplePay'
import { AdyenCreditCardButton, AdyenCreditCardFormComponent } from '@PaymentComponents/Adyen/Components/CreditCard/AdyenCreditCard'
import { AdyenPaypalButton } from '@PaymentComponents/Adyen/Components/Paypal/AdyenPaypal'
import { AdyenPresentToShopperButton } from '@PaymentComponents/Adyen/Components/PresentToShopper/AdyenPresentToShopper'
import { AdyenRedirectButton } from '@PaymentComponents/Adyen/Components/Redirect/AdyenRedirect'
import { AdyenSelectorButton, AdyenSelectorInfoComponent } from '@PaymentComponents/Adyen/Components/Selector/AdyenWithSelector'
import { BraintreeApplePayButton } from '@PaymentComponents/Braintree/Components/ApplePay/BraintreeApplePay'
import { BraintreeCreditCardButton, BraintreeCreditCardInfoComponent } from '@PaymentComponents/Braintree/Components/CreditCard/BraintreeCreditCard'
import { BraintreePaypalButton } from '@PaymentComponents/Braintree/Components/Paypal/BraintreePaypal'
import { BraintreeVenmoButton } from '@PaymentComponents/Braintree/Components/Venmo/BraintreeVenmo'
import { ByJunoButton, ByJunoInfoComponent } from '@PaymentComponents/Default/Components/ByJuno/ByJuno'
import { PostFinanceButton } from '@PaymentComponents/Default/Components/PostFinance/PostFinance'
import { EpayViaBillButton } from '@PaymentComponents/Default/Components/ViaBill/EpayViaBill'

type PaymentSubmit = <T>(paymentMethodData: T | null, submitCallback: PaymentSubmitCallback) => void
export interface CheckoutPaymentComponentProps {
  grandTotal: CheckoutCartV2Total
  option: CheckoutV2PaymentMethod
  submitPayment: PaymentSubmit
  submitWithoutData?: boolean
}
interface PaymentComponentFactory {
  getButtonComponents: (option: CheckoutV2PaymentMethod, submitPayment: PaymentSubmit, grandTotal: CheckoutCartV2Total) => ReactNode
  getFormComponents: (option: CheckoutV2PaymentMethod, submitPayment: PaymentSubmit, grandTotal: CheckoutCartV2Total) => ReactNode
}

const useAdyenComponentFactory: () => PaymentComponentFactory = () => {
  const getFormComponents = useCallback((option: CheckoutV2PaymentMethod, submitPayment: PaymentSubmit, grandTotal: CheckoutCartV2Total) => {
    switch (option.type) {
      case 'credit_card':
        return <AdyenCreditCardFormComponent key={option.id} grandTotal={grandTotal} option={option} submitPayment={submitPayment} />
      case 'bcmc':
        return <AdyenCreditCardFormComponent key={option.id} grandTotal={grandTotal} option={option} submitPayment={submitPayment} />
      case 'onlinebanking_pl':
        return (
          <AdyenSelectorInfoComponent
            key={option.id}
            grandTotal={grandTotal}
            option={{ ...option, type: 'onlineBanking_PL' }}
            submitPayment={submitPayment}
          />
        )
      case 'onlinebanking_cz':
        return (
          <AdyenSelectorInfoComponent
            key={option.id}
            grandTotal={grandTotal}
            option={{ ...option, type: 'onlineBanking_CZ' }}
            submitPayment={submitPayment}
          />
        )
      case 'onlinebanking_sk':
        return (
          <AdyenSelectorInfoComponent
            key={option.id}
            grandTotal={grandTotal}
            option={{ ...option, type: 'onlineBanking_SK' }}
            submitPayment={submitPayment}
          />
        )
      case 'ideal':
        return <AdyenSelectorInfoComponent key={option.id} grandTotal={grandTotal} option={option} submitPayment={submitPayment} />
      default:
        return null
    }
  }, [])
  const getButtonComponents = useCallback((option: CheckoutV2PaymentMethod, submitPayment: PaymentSubmit, grandTotal: CheckoutCartV2Total) => {
    switch (option.type) {
      case 'credit_card':
        return <AdyenCreditCardButton grandTotal={grandTotal} option={option} submitPayment={submitPayment} />
      case 'bcmc':
        return <AdyenCreditCardButton key={option.id} grandTotal={grandTotal} option={option} submitPayment={submitPayment} />
      case 'paypal':
        return <AdyenPaypalButton key={option.id} grandTotal={grandTotal} option={option} submitPayment={submitPayment} />
      case 'applepay':
        return <AdyenApplePayButton key={option.id} grandTotal={grandTotal} option={option} submitPayment={submitPayment} />
      case 'onlinebanking_pl':
        return <AdyenSelectorButton key={option.id} grandTotal={grandTotal} option={{ ...option, type: 'onlineBanking_PL' }} submitPayment={submitPayment} />
      case 'onlinebanking_cz':
        return <AdyenSelectorButton key={option.id} grandTotal={grandTotal} option={{ ...option, type: 'onlineBanking_CZ' }} submitPayment={submitPayment} />
      case 'onlinebanking_sk':
        return <AdyenSelectorButton key={option.id} grandTotal={grandTotal} option={{ ...option, type: 'onlineBanking_SK' }} submitPayment={submitPayment} />
      case 'ideal':
        return <AdyenSelectorButton key={option.id} grandTotal={grandTotal} option={option} submitPayment={submitPayment} />
      case 'multibanco':
        return <AdyenPresentToShopperButton key={option.id} grandTotal={grandTotal} option={option} submitPayment={submitPayment} />
      case 'mbway':
        return <AdyenRedirectButton key={option.id} grandTotal={grandTotal} option={option} submitPayment={submitPayment} />
      case 'ebanking_fi':
        return <AdyenRedirectButton key={option.id} grandTotal={grandTotal} option={{ ...option, type: 'ebanking_FI' }} submitPayment={submitPayment} />
      case 'direct_ebanking':
        return <AdyenRedirectButton key={option.id} grandTotal={grandTotal} option={{ ...option, type: 'directEbanking' }} submitPayment={submitPayment} />
      case 'bcmc_mobile':
        return (
          <AdyenRedirectButton
            key={option.id}
            grandTotal={grandTotal}
            option={{ ...option, type: getDeviceType() === 'desktop' ? 'bcmc_mobile_QR' : 'bcmc_mobile' }}
            submitPayment={submitPayment}
          />
        )
      default:
        return <AdyenRedirectButton key={option.id} grandTotal={grandTotal} option={option} submitPayment={submitPayment} />
    }
  }, [])
  return { getFormComponents, getButtonComponents }
}

const useBillwerkComponentFactory: () => PaymentComponentFactory = () => {
  const getFormComponents = useCallback((option: CheckoutV2PaymentMethod) => {
    switch (option.type) {
      default:
        return null
    }
  }, [])
  const getButtonComponents = useCallback((option: CheckoutV2PaymentMethod, submitPayment: PaymentSubmit, grandTotal: CheckoutCartV2Total) => {
    switch (option.type) {
      default:
        return <BillwerkRedirectButton key={option.id} grandTotal={grandTotal} option={option} submitPayment={submitPayment} />
    }
  }, [])
  return { getFormComponents, getButtonComponents }
}

const useBraintreeComponentFactory: () => PaymentComponentFactory = () => {
  const getFormComponents = useCallback((option: CheckoutV2PaymentMethod, submitPayment: PaymentSubmit, grandTotal: CheckoutCartV2Total) => {
    switch (option.type) {
      case 'credit_card':
        return <BraintreeCreditCardInfoComponent key={option.id} grandTotal={grandTotal} option={option} submitPayment={submitPayment} />
      default:
        return null
    }
  }, [])
  const getButtonComponents = useCallback((option: CheckoutV2PaymentMethod, submitPayment: PaymentSubmit, grandTotal: CheckoutCartV2Total) => {
    switch (option.type) {
      case 'credit_card':
        return <BraintreeCreditCardButton key={option.id} grandTotal={grandTotal} option={option} submitPayment={submitPayment} />
      case 'applepay':
        return <BraintreeApplePayButton key={option.id} grandTotal={grandTotal} option={option} submitPayment={submitPayment} />
      case 'paypal':
        return <BraintreePaypalButton key={option.id} grandTotal={grandTotal} option={option} submitPayment={submitPayment} />
      case 'venmo':
        return <BraintreeVenmoButton key={option.id} grandTotal={grandTotal} option={option} submitPayment={submitPayment} />
      default:
        return null
    }
  }, [])
  return { getFormComponents, getButtonComponents }
}
const useDefaultPaymentComponentFactory: () => PaymentComponentFactory = () => {
  const getFormComponents = useCallback((option: CheckoutV2PaymentMethod, submitPayment: PaymentSubmit, grandTotal: CheckoutCartV2Total) => {
    switch (`${option.id}_${option.type}`) {
      case 'cdp_invoice_cdp_invoice':
        return <ByJunoInfoComponent key={option.id} grandTotal={grandTotal} option={option} submitPayment={submitPayment} />
      default:
        return null
    }
  }, [])
  const getButtonComponents = useCallback((option: CheckoutV2PaymentMethod, submitPayment: PaymentSubmit, grandTotal: CheckoutCartV2Total) => {
    switch (`${option.id}_${option.type}`) {
      case 'epay_standard_7':
        return <EpayViaBillButton key={option.id} grandTotal={grandTotal} option={option} submitPayment={submitPayment} />
      case 'cdp_invoice_cdp_invoice':
        return <ByJunoButton key={option.id} grandTotal={grandTotal} option={option} submitPayment={submitPayment} />
      case 'postfinancecw_postfinanceefinance_postfinancecw_postfinanceefinance':
        return <PostFinanceButton key={option.id} grandTotal={grandTotal} option={option} submitPayment={submitPayment} />
      case 'postfinancecw_postfinancecard_postfinancecw_postfinancecard':
        return <PostFinanceButton key={option.id} grandTotal={grandTotal} option={option} submitPayment={submitPayment} />
      default:
        return null
    }
  }, [])
  return { getFormComponents, getButtonComponents }
}

export const usePaymentComponentFactory:
(grandTotal?: CheckoutCartV2Total, loading?: boolean) => PaymentComponentFactory = (grandTotal?: CheckoutCartV2Total, loading?: boolean) => {
  const { getButtonComponents: getAdyenButtonComponents, getFormComponents: getAdyenFormComponents } = useAdyenComponentFactory()
  const { getButtonComponents: getBraintreeButtonComponents, getFormComponents: getBraintreeFormComponents } = useBraintreeComponentFactory()
  const { getButtonComponents: getDefaultButtonComponents, getFormComponents: getDefaultFormComponents } = useDefaultPaymentComponentFactory()
  const { getButtonComponents: getBillwerkButtonComponents } = useBillwerkComponentFactory()
  const getFormComponents = useCallback((option: CheckoutV2PaymentMethod, submitPayment: PaymentSubmit) => {
    if (!grandTotal || loading) {
      return null
    }
    switch ((option.provider || '').toLowerCase()) {
      case 'adyen':
        return getAdyenFormComponents(option, submitPayment, grandTotal)
      case 'braintree':
        return getBraintreeFormComponents(option, submitPayment, grandTotal)
      default:
        return getDefaultFormComponents(option, submitPayment, grandTotal)
    }
  }, [getAdyenFormComponents, getBraintreeFormComponents, getDefaultFormComponents, grandTotal, loading])
  const getButtonComponents = useCallback((option: CheckoutV2PaymentMethod, submitPayment: PaymentSubmit) => {
    if (!grandTotal || loading) {
      return null
    }
    switch ((option.provider || '').toLowerCase()) {
      case 'adyen':
        return getAdyenButtonComponents(option, submitPayment, grandTotal)
      case 'braintree':
        return getBraintreeButtonComponents(option, submitPayment, grandTotal)
      case 'billwerk':
        return getBillwerkButtonComponents(option, submitPayment, grandTotal)
      default:
        return getDefaultButtonComponents(option, submitPayment, grandTotal)
    }
  }, [getAdyenButtonComponents, getBraintreeButtonComponents, getDefaultButtonComponents, getBillwerkButtonComponents, grandTotal, loading])
  return { getFormComponents, getButtonComponents }
}
